import { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react'
import { translations, supportedLanguages } from '../i18n'
import { parseLocalePathname } from '../lib/localeSeoRoutes'

/** When Provider is missing (e.g. HMR), still expose real language list so selectors do not crash. */
const SUPPORTED_FALLBACK = supportedLanguages

const STORAGE_KEY = 'app_lang'

const LanguageContext = createContext(null)

function getStoredLanguage() {
  if (typeof window === 'undefined') return 'en'
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && translations[stored]) return stored
  } catch (_) {}
  return 'en'
}

function getInitialLanguage() {
  if (typeof window === 'undefined') return 'en'
  const { locale } = parseLocalePathname(window.location.pathname)
  if (translations[locale]) return locale
  return getStoredLanguage()
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(getInitialLanguage)

  useEffect(() => {
    console.debug('[LanguageContext] Current language state:', language)
  }, [language])

  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    if (language === 'ar') {
      root.setAttribute('dir', 'rtl')
      root.setAttribute('lang', 'ar')
    } else {
      root.setAttribute('dir', 'ltr')
      root.setAttribute('lang', language === 'en' ? 'en' : language)
    }
  }, [language])

  const setLanguage = useCallback((lang) => {
    const hasTranslations = !!translations[lang]
    console.debug('[LanguageContext] setLanguage called', { lang, hasTranslations, availableCodes: Object.keys(translations) })
    if (!hasTranslations) {
      console.warn('[LanguageContext] Ignoring setLanguage ,  no translations for', lang)
      return
    }
    setLanguageState(lang)
    try {
      localStorage.setItem(STORAGE_KEY, lang)
    } catch (_) {}
    console.debug('[LanguageContext] Language updated to', lang)
  }, [])

  const t = useCallback(
    (key, params) => {
      let value = translations[language]?.[key] ?? translations.en?.[key] ?? key
      if (params && typeof value === 'string') {
        value = value.replace(/\{\{(\w+)\}\}/g, (_, k) =>
          params[k] !== undefined && params[k] !== null ? String(params[k]) : `{{${k}}}`
        )
      }
      return value
    },
    [language]
  )

  const value = useMemo(
    () => ({ language, setLanguage, t, supportedLanguages }),
    [language, setLanguage, t]
  )

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

const FALLBACK_CONTEXT = {
  language: 'en',
  setLanguage: () => {},
  t: (key) => key,
  supportedLanguages: SUPPORTED_FALLBACK,
}

export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx) {
    if (import.meta.env.DEV) {
      console.warn('[useLanguage] Context not found ,  returning fallback. This usually means a Vite HMR module split occurred. Refreshing the page will fix it.')
    }
    return FALLBACK_CONTEXT
  }
  return ctx
}

// Prevent Vite HMR from splitting the context instance across hot reloads.
// A full page reload is safer than a partial module swap for context providers.
if (import.meta.hot) {
  import.meta.hot.decline()
}
